/**
 * Bundle Alternates Modal System
 * 
 * Manages item swapping for out-of-stock bundle items.
 * Supports multiple swaps in sequence (queue system).
 * 
 * Components:
 * - BundleAlternatesManager: Singleton that coordinates modal state and swap queue
 * - BundleAlternatesModal: Web Component that renders the modal UI
 * 
 * Events Dispatched:
 * - bundle:swap-confirmed - When a single swap is confirmed
 * - bundle:swap-skipped - When user skips a swap (keeps original)
 * - bundle:swaps-complete - When all swaps are done
 * - bundle:swaps-cancelled - When user cancels all swaps
 */

import sizeMapping from './size-mapping.js';

// DEBUG MODE - Set to true to enable console logging
const DEBUG_MODE = false;

const debugLog = (...args) => DEBUG_MODE && console.log(...args);
const debugWarn = (...args) => DEBUG_MODE && console.warn(...args);
const debugError = (...args) => console.error(...args); // Always show errors

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Normalize size display: replace XXL with 2XL and XXXL with 3XL
 * @param {string|Object} size 
 * @returns {string|Object}
 */
function normalizeSize(size) {
  if (!size) return size;
  
  // Handle object format (waist-inseam)
  if (typeof size === 'object') {
    return {
      ...size,
      combined: size.combined ? size.combined.replace(/\bXXXL\b/gi, '3XL').replace(/\bXXL\b/gi, '2XL') : size.combined
    };
  }
  
  // Handle string format
  if (typeof size === 'string') {
    return size
      .replace(/\bXXXL\b/gi, '3XL')
      .replace(/\bXXL\b/gi, '2XL');
  }
  
  return size;
}

/**
 * Format size for display, adding inch symbol for waist-inseam sizes
 * Converts "32x30" to "32"x30" format
 * @param {string|Object} size - The size value (string or object with combined property)
 * @returns {string} - Formatted size string for display
 */
function formatSizeForDisplay(size) {
  if (!size) return '';
  
  // First normalize the size (XXL → 2XL, etc.)
  const normalized = normalizeSize(size);
  
  // Get the string value to format
  let sizeStr = typeof normalized === 'object' ? normalized.combined : normalized;
  
  if (!sizeStr || typeof sizeStr !== 'string') return sizeStr || '';
  
  // Check if it's a waist-inseam format (e.g., "32x30", "34x32")
  const waistInseamMatch = sizeStr.match(/^(\d+)x(\d+)$/i);
  if (waistInseamMatch) {
    const waist = waistInseamMatch[1];
    const inseam = waistInseamMatch[2];
    return `${waist}"x${inseam}`;
  }
  
  return sizeStr;
}

/**
 * Remove vendor name from product title if present
 * Handles common patterns like "Vendor - Title", "Vendor | Title", "Title by Vendor", etc.
 * @param {string} title - The product title
 * @param {string} vendor - The vendor name
 * @returns {string} - The cleaned title
 */
function removeVendorFromTitle(title, vendor) {
  if (!title || typeof title !== 'string') return title;
  if (!vendor || typeof vendor !== 'string') return title;
  
  let cleanTitle = title;
  
  // Remove common patterns: "Vendor - ", "Vendor | ", "Vendor: "
  cleanTitle = cleanTitle.replace(new RegExp(`^${escapeRegExp(vendor)}\\s*[-|:]\\s*`, 'i'), '');
  
  // Remove patterns at the end: " by Vendor", " from Vendor"
  cleanTitle = cleanTitle.replace(new RegExp(`\\s+(by|from)\\s+${escapeRegExp(vendor)}$`, 'i'), '');
  
  // Remove standalone vendor name (case-insensitive)
  cleanTitle = cleanTitle.replace(new RegExp(`^${escapeRegExp(vendor)}\\s*`, 'i'), '');
  cleanTitle = cleanTitle.replace(new RegExp(`\\s*${escapeRegExp(vendor)}$`, 'i'), '');
  
  return cleanTitle.trim();
}

/**
 * Escape special regex characters in a string
 * @param {string} string 
 * @returns {string}
 */
function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// ============================================================================
// BUNDLE ALTERNATES MANAGER (Singleton)
// ============================================================================
class BundleAlternatesManager {
  static instance = null;
  
  constructor() {
    if (BundleAlternatesManager.instance) {
      return BundleAlternatesManager.instance;
    }
    
    this.swapQueue = [];
    this.currentSwap = null;
    this.swaps = new Map(); // originalProductId -> replacement data
    this.modal = null;
    this.totalSwapsNeeded = 0;
    
    BundleAlternatesManager.instance = this;
  }
  
  static getInstance() {
    if (!BundleAlternatesManager.instance) {
      new BundleAlternatesManager();
    }
    return BundleAlternatesManager.instance;
  }
  
  /**
   * Register the modal element with the manager
   * @param {BundleAlternatesModal} modalElement 
   */
  setModal(modalElement) {
    this.modal = modalElement;
    debugLog('[BundleAlternatesManager] Modal registered');
  }
  
  /**
   * Queue items for swapping
   * @param {Array} items - Array of {productId, productTitle, itemType, selectedSize, featuredImage}
   * @param {Object} options - Configuration options
   * @param {string} options.mode - 'oos' (out of stock) or 'optional' (user-initiated swap)
   */
  queueSwaps(items, options = {}) {
    debugLog('[BundleAlternatesManager] Queueing swaps for', items.length, 'items', 'mode:', options.mode);
    
    this.swapQueue = [...items];
    this.swaps.clear();
    this.totalSwapsNeeded = items.length;
    this.swapMode = options.mode || 'oos'; // Default to OOS mode
    
    if (this.swapQueue.length > 0) {
      this.processNextSwap();
    }
  }
  
  /**
   * Add a single item to the queue
   * @param {Object} item 
   */
  addToQueue(item) {
    this.swapQueue.push(item);
    this.totalSwapsNeeded++;
    
    if (!this.currentSwap && this.modal) {
      this.processNextSwap();
    }
  }
  
  /**
   * Process next item in queue
   */
  processNextSwap() {
    if (this.swapQueue.length === 0) {
      this.finishSwapping();
      return;
    }
    
    this.currentSwap = this.swapQueue.shift();
    
    debugLog('[BundleAlternatesManager] Processing swap:', this.currentSwap.productTitle);
    
    if (this.modal) {
      const queuePosition = this.swaps.size + 1;
      const queueTotal = this.totalSwapsNeeded;
      
      this.modal.showForItem(this.currentSwap, {
        queuePosition,
        queueTotal,
        mode: this.swapMode || 'oos'
      });
    } else {
      debugError('[BundleAlternatesManager] No modal registered, skipping swap');
      this.skipSwap();
    }
  }
  
  /**
   * Confirm current swap selection
   * @param {number|string} newVariantId - The new variant ID
   * @param {Object} newProductData - Data about the new product
   */
  confirmSwap(newVariantId, newProductData) {
    if (!this.currentSwap) {
      debugWarn('[BundleAlternatesManager] No current swap to confirm');
      return;
    }
    
    debugLog('[BundleAlternatesManager] Swap confirmed:', 
      this.currentSwap.productTitle, '→', newProductData.productTitle);
    
    this.swaps.set(this.currentSwap.productId, {
      originalProductId: this.currentSwap.productId,
      originalProductTitle: this.currentSwap.productTitle,
      originalItemType: this.currentSwap.itemType,
      newVariantId,
      ...newProductData
    });
    
    // Dispatch event for listeners
    document.dispatchEvent(new CustomEvent('bundle:swap-confirmed', {
      detail: {
        original: { ...this.currentSwap },
        replacement: { 
          variantId: newVariantId, 
          ...newProductData 
        }
      }
    }));
    
    this.currentSwap = null;
    this.processNextSwap();
  }
  
  /**
   * Skip current swap (keep original OOS item)
   */
  skipSwap() {
    if (!this.currentSwap) {
      debugWarn('[BundleAlternatesManager] No current swap to skip');
      return;
    }
    
    debugLog('[BundleAlternatesManager] Swap skipped:', this.currentSwap.productTitle);
    
    document.dispatchEvent(new CustomEvent('bundle:swap-skipped', {
      detail: { item: { ...this.currentSwap } }
    }));
    
    this.currentSwap = null;
    this.processNextSwap();
  }
  
  /**
   * Cancel all swaps and close modal
   */
  cancelAll() {
    debugLog('[BundleAlternatesManager] All swaps cancelled');
    
    this.swapQueue = [];
    this.currentSwap = null;
    
    if (this.modal) {
      this.modal.hide();
    }
    
    document.dispatchEvent(new CustomEvent('bundle:swaps-cancelled'));
  }
  
  /**
   * Finish swapping process - called when queue is empty
   */
  finishSwapping() {
    debugLog('[BundleAlternatesManager] All swaps complete. Total swaps made:', this.swaps.size);
    
    if (this.modal) {
      this.modal.hide();
    }
    
    const swapsArray = Array.from(this.swaps.entries()).map(([origId, data]) => ({
      originalProductId: origId,
      ...data
    }));
    
    document.dispatchEvent(new CustomEvent('bundle:swaps-complete', {
      detail: {
        swaps: swapsArray,
        swapCount: swapsArray.length
      }
    }));
    
    // Reset for next use
    this.totalSwapsNeeded = 0;
  }
  
  /**
   * Get all completed swaps
   * @returns {Map}
   */
  getSwaps() {
    return new Map(this.swaps);
  }
  
  /**
   * Get swaps as array
   * @returns {Array}
   */
  getSwapsArray() {
    return Array.from(this.swaps.entries()).map(([origId, data]) => ({
      originalProductId: origId,
      ...data
    }));
  }
  
  /**
   * Check if there are pending swaps
   * @returns {boolean}
   */
  hasPendingSwaps() {
    return this.swapQueue.length > 0 || this.currentSwap !== null;
  }
  
  /**
   * Clear all swap data and reset state
   */
  reset() {
    this.swapQueue = [];
    this.currentSwap = null;
    this.swaps.clear();
    this.totalSwapsNeeded = 0;
  }
}


/**
 * Load theme settings for alternates modal text strings
 * Falls back to defaults if settings are not available
 * @returns {Object}
 */
function loadAlternatesSettings() {
  const defaultSettings = {
    oos: {
      title: "This item isn't available in your size",
      description: "— but we found a great match recommended by your stylist.",
      confirmButton: "Save Replacement"
    },
    optional: {
      title: "Suggested Alternates",
      description: "— Easily swap with a similar piece recommended by your stylist.",
      confirmButton: "Confirm Swap"
    },
    zeroSizeMatches: {
      title: "No size matches found",
      description: "— Take your style quiz and one of our stylists will build a custom bundle for you.",
      confirmButton: "Take Style Quiz"
    },
    recommendedBadge: "Recommended",
    emptyState: "No alternates available for this item in your size.",
    selectLegend: "Select an alternate item"
  };

  try {
    const settingsEl = document.getElementById('bundle-alternates-settings');
    if (settingsEl) {
      const parsed = JSON.parse(settingsEl.textContent);
      // Deep merge for nested oos/optional/zeroSizeMatches objects
      return {
        oos: { ...defaultSettings.oos, ...parsed.oos },
        optional: { ...defaultSettings.optional, ...parsed.optional },
        zeroSizeMatches: { ...defaultSettings.zeroSizeMatches, ...parsed.zeroSizeMatches },
        recommendedBadge: parsed.recommendedBadge || defaultSettings.recommendedBadge,
        emptyState: parsed.emptyState || defaultSettings.emptyState,
        selectLegend: parsed.selectLegend || defaultSettings.selectLegend
      };
    }
  } catch (e) {
    debugWarn('[BundleAlternatesModal] Could not parse settings, using defaults:', e);
  }

  return defaultSettings;
}

// ============================================================================
// BUNDLE ALTERNATES MODAL (Web Component)
// ============================================================================
class BundleAlternatesModal extends HTMLElement {
  constructor() {
    super();
    
    this.alternatesData = [];
    this.selectedAlternate = null;
    this.currentItem = null;
    this.settings = null; // Loaded in connectedCallback
    
    // Bound methods for event listeners
    this.handleClose = this.handleClose.bind(this);
    this.handleRadioChange = this.handleRadioChange.bind(this);
    this.handleConfirm = this.handleConfirm.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handleFocusTrap = this.handleFocusTrap.bind(this);
    this.handleLabelKeydown = this.handleLabelKeydown.bind(this);
    this.handleEmptyAction = this.handleEmptyAction.bind(this);
    
    // Store reference to previously focused element for focus restoration
    this.previouslyFocusedElement = null;
  }
  
  connectedCallback() {
    debugLog('[BundleAlternatesModal] Connected');
    
    // Load CMS settings for text strings
    this.settings = loadAlternatesSettings();
    
    // Parse alternates data from JSON
    this.parseAlternatesData();
    
    // Cache element references
    this.cacheElements();
    
    // Attach event listeners
    this.attachListeners();
    
    // Register with manager
    BundleAlternatesManager.getInstance().setModal(this);
  }
  
  disconnectedCallback() {
    this.detachListeners();
  }
  
  /**
   * Cache DOM element references
   */
  cacheElements() {
    this.overlay = this.querySelector('.bundle-alternates-modal__overlay');
    this.closeBtn = this.querySelector('.bundle-alternates-modal__close');
    this.grid = this.querySelector('[data-alternates-grid]');
    this.confirmBtn = this.querySelector('[data-confirm-swap]');
    this.emptyState = this.querySelector('[data-empty-state]');
    this.emptyLink = this.querySelector('[data-empty-link]');
    this.zeroStateLink = this.querySelector('[data-zero-state-link]');
    this.currentImageEl = this.querySelector('[data-current-image]');
    this.currentTitleEl = this.querySelector('[data-current-title]');
    // Option wrapper divs (contain data attributes)
    this.options = this.querySelectorAll('.bundle-alternates-modal__option');
    // Radio inputs
    this.radios = this.querySelectorAll('.bundle-alternates-modal__radio');
  }
  
  /**
   * Attach event listeners
   */
  attachListeners() {
    this.overlay?.addEventListener('click', this.handleOverlayClick);
    this.closeBtn?.addEventListener('click', this.handleClose);
    this.confirmBtn?.addEventListener('click', this.handleConfirm);
    
    // Listen for radio changes
    this.radios?.forEach(radio => {
      radio.addEventListener('change', this.handleRadioChange);
    });
    
    // Listen for keyboard events on label cards (Enter/Space to select)
    this.labelCards = this.querySelectorAll('.bundle-alternates-modal__label-card');
    this.labelCards?.forEach(label => {
      label.addEventListener('keydown', this.handleLabelKeydown);
    });

    // Listen for empty-state button click (zero alternates → select next sibling)
    this.emptyLink?.addEventListener('click', this.handleEmptyAction);
  }
  
  /**
   * Detach event listeners
   */
  detachListeners() {
    this.overlay?.removeEventListener('click', this.handleOverlayClick);
    this.closeBtn?.removeEventListener('click', this.handleClose);
    this.confirmBtn?.removeEventListener('click', this.handleConfirm);
    
    this.radios?.forEach(radio => {
      radio.removeEventListener('change', this.handleRadioChange);
    });
    
    this.labelCards?.forEach(label => {
      label.removeEventListener('keydown', this.handleLabelKeydown);
    });

    this.emptyLink?.removeEventListener('click', this.handleEmptyAction);
    
    document.removeEventListener('keydown', this.handleKeydown);
  }
  
  /**
   * Parse alternates data from the embedded JSON script or sessionStorage
   * 
   * The new bundle-data.liquid snippet outputs alternates grouped by itemNumber:
   * {
   *   "alternates": [
   *     { "itemNumber": 1, "variants": [...] },
   *     { "itemNumber": 2, "variants": [...] }
   *   ]
   * }
   * 
   * We flatten this into a single array with itemNumber on each variant for filtering.
   */
  parseAlternatesData() {
    // First, try to get from new bundle-data.liquid JSON (bundle PDP)
    const newDataEl = document.getElementById('bundle-alternates-data');
    if (newDataEl) {
      try {
        const data = JSON.parse(newDataEl.textContent);
        
        // Handle the new grouped structure from bundle-data.liquid
        if (data.alternates && Array.isArray(data.alternates)) {
          // Flatten the grouped structure into a single array
          this.alternatesData = [];
          data.alternates.forEach(group => {
            if (group.variants && Array.isArray(group.variants)) {
              // Each variant already has itemNumber from the snippet
              this.alternatesData.push(...group.variants);
            }
          });
          
          debugLog('[BundleAlternatesModal] Parsed', this.alternatesData.length, 'alternates from bundle-data.liquid (new structure)');
          debugLog('[BundleAlternatesModal] Alternates by itemNumber:', this.getAlternatesByItemNumber());
          return;
        }
      } catch (e) {
        debugError('[BundleAlternatesModal] Failed to parse bundle-data.liquid JSON:', e);
      }
    }
    
    // Fallback: try legacy JSON format (data-alternates-json attribute)
    const legacyDataEl = this.querySelector('[data-alternates-json]');
    if (legacyDataEl) {
      try {
        const data = JSON.parse(legacyDataEl.textContent);
        this.alternatesData = data.alternates || [];
        debugLog('[BundleAlternatesModal] Parsed', this.alternatesData.length, 'alternates from legacy Liquid');
        debugLog('[BundleAlternatesModal] Alternates data:', this.alternatesData);
        return;
      } catch (e) {
        debugError('[BundleAlternatesModal] Failed to parse alternates data:', e);
        debugError('[BundleAlternatesModal] Raw JSON content:', legacyDataEl.textContent?.substring(0, 500));
      }
    }
    
    // Fallback: try to get from sessionStorage (checkout page)
    try {
      const savedBundle = sessionStorage.getItem('bundle_selections');
      if (savedBundle) {
        const bundleData = JSON.parse(savedBundle);
        if (bundleData.alternates && bundleData.alternates.length > 0) {
          this.alternatesData = bundleData.alternates;
          debugLog('[BundleAlternatesModal] Loaded', this.alternatesData.length, 'alternates from sessionStorage');
          return;
        }
      }
    } catch (e) {
      debugError('[BundleAlternatesModal] Error reading alternates from sessionStorage:', e);
    }
    
    debugWarn('[BundleAlternatesModal] No alternates data found (Liquid or sessionStorage)');
    this.alternatesData = [];
  }
  
  /**
   * Get alternates grouped by itemNumber (for debugging)
   * @returns {Object} Object keyed by itemNumber
   */
  getAlternatesByItemNumber() {
    const grouped = {};
    this.alternatesData.forEach(alt => {
      const num = alt.itemNumber || 'unknown';
      if (!grouped[num]) grouped[num] = [];
      grouped[num].push(alt);
    });
    return grouped;
  }
  
  /**
   * Get mode-specific copy configuration from loaded settings
   * - oos: Out of stock - item not available in selected size (PDP)
   * - optional: User-initiated optional swap (Checkout/Preflight)
   * @param {string} mode - 'oos' or 'optional'
   * @returns {Object} Configuration with title, description, confirmButton
   */
  getModeConfig(mode) {
    // Use loaded settings, falling back to defaults
    const settings = this.settings || loadAlternatesSettings();
    return settings[mode] || settings.oos;
  }

  /**
   * Show modal for a specific item
   * @param {Object} item - {productId, productTitle, itemNumber, categoryKey, sizingType, selectedSize, featuredImage, originalItem}
   * @param {Object} queueInfo - {queuePosition, queueTotal, mode}
   */
  showForItem(item, queueInfo = {}) {
    debugLog('[BundleAlternatesModal] Showing for item:', item.productTitle);
    debugLog('[BundleAlternatesModal] ItemNumber:', item.itemNumber);
    debugLog('[BundleAlternatesModal] Category:', item.category, '| CategoryKey:', item.categoryKey);
    debugLog('[BundleAlternatesModal] SizingType:', item.sizingType, '| SelectedSize:', item.selectedSize);
    debugLog('[BundleAlternatesModal] Mode:', queueInfo.mode);
    debugLog('[BundleAlternatesModal] Original item (for swap back):', item.originalItem?.productTitle || 'none');
    
    this.currentItem = item;
    this.selectedAlternate = null;
    this.currentMode = queueInfo.mode || 'oos';
    
    // Store original item for "swap back" functionality
    this.originalItem = item.originalItem || null;
    
    // Apply mode-specific copy
    this.applyModeCopy(this.currentMode);
    
    // If no pre-rendered options exist (checkout page), generate them dynamically
    if (!this.options || this.options.length === 0) {
      debugLog('[BundleAlternatesModal] No pre-rendered options, generating dynamically');
      this.generateOptionsFromData();
    }
    
    // Clear all radio selections
    this.clearRadioSelections();
    
    // Update header with current item info
    this.updateCurrentItemDisplay(item);
    
    // Filter and show relevant alternates
    // NEW: Use itemNumber as primary filter when available (more reliable than category)
    // Falls back to category-based filtering for backwards compatibility
    const filterResult = this.filterAlternates(
      item.itemNumber,  // Primary identifier for the bundle slot (1-6)
      item.categoryKey || item.itemType,  // Fallback: category or legacy itemType
      item.selectedSize,
      item.sizingType || this.deriveSizingType(item.itemType),
      item.type || null  // Product type for type-aware filtering
    );
    let visibleCount = filterResult.visibleCount;
    
    // If there's an original item (user has already swapped), add it as an option to swap back
    if (this.originalItem) {
      const originalOptionAdded = this.addOriginalItemOption(item.selectedSize, item.sizingType);
      if (originalOptionAdded) {
        visibleCount++;
      }
    }
    
    // Disable confirm button until a selection is made
    if (this.confirmBtn) {
      this.confirmBtn.disabled = true;
    }
    
    // Show/hide empty state
    this.toggleEmptyState(visibleCount === 0);
    
    // If no alternates are available in the user's size, override the title/description
    // with the zeroSizeMatches copy from theme settings
    if (visibleCount === 0) {
      this.applyZeroSizeMatchesCopy();
    }
    
    // Show modal
    this.show();
  }
  
  /**
   * Derive sizingType from legacy itemType for backwards compatibility
   * @param {string} itemType - Legacy item type ('top', 'bottom', 'shoe', 'unknown')
   * @returns {string} sizingType ('standard', 'waist-inseam', 'shoe')
   */
  deriveSizingType(itemType) {
    switch (itemType) {
      case 'bottom':
        return 'waist-inseam';
      case 'shoe':
        return 'shoe';
      default:
        return 'standard';
    }
  }
  
  /**
   * Apply mode-specific copy to modal elements
   * @param {string} mode - 'oos' or 'optional'
   */
  applyModeCopy(mode) {
    const config = this.getModeConfig(mode);
    
    // Update title
    const titleEl = this.querySelector('.bundle-alternates-modal__title');
    if (titleEl) {
      titleEl.textContent = config.title;
    }
    
    // Update description
    const descEl = this.querySelector('.bundle-alternates-modal__description');
    if (descEl) {
      descEl.textContent = config.description;
    }
    
    // Update confirm button
    if (this.confirmBtn) {
      this.confirmBtn.textContent = config.confirmButton;
    }
  }
  
  /**
   * Apply "zero size matches" copy to the modal when no alternates match the user's size.
   * Uses the zeroSizeMatches settings from theme settings (title, description, confirmButton).
   */
  applyZeroSizeMatchesCopy() {
    const settings = this.settings || loadAlternatesSettings();
    const config = settings.zeroSizeMatches;
    
    if (!config) return;
    
    // Update title
    const titleEl = this.querySelector('.bundle-alternates-modal__title');
    if (titleEl) {
      titleEl.textContent = config.title;
    }
    
    // Update description
    const descEl = this.querySelector('.bundle-alternates-modal__description');
    if (descEl) {
      descEl.textContent = config.description;
    }
    
    // Note: the empty-state button text is rendered by Liquid from the
    // bundle_alternates_zero_size_browse_button_text theme setting.
    // We intentionally do NOT overwrite it here so the Liquid value is preserved.
  }

  /**
   * Handle the zero-alternates empty-state button click.
   * On the PDP: closes the modal and selects the first non-active sibling.
   * On the checkout page (data-empty-action="back"): navigates back to the PDP.
   */
  handleEmptyAction(event) {
    event.preventDefault();

    // Check if this button is configured to go back (checkout page)
    const action = this.emptyLink?.dataset.emptyAction;
    if (action === 'back') {
      this.hide();
      if (window.history.length > 1) {
        window.history.back();
      } else {
        window.location.href = '/';
      }
      return;
    }

    // PDP flow: close the modal and select the next sibling
    this.hide();

    const siblingPicker = document.querySelector('bundle-sibling-picker');
    if (!siblingPicker) {
      debugWarn('[BundleAlternatesModal] No sibling picker found on page');
      return;
    }

    const siblingOptions = siblingPicker.querySelectorAll('.bundle-product__sibling-option');
    let nextSibling = null;

    for (const option of siblingOptions) {
      if (!option.classList.contains('is-active')) {
        nextSibling = option;
        break;
      }
    }

    if (nextSibling) {
      debugLog('[BundleAlternatesModal] Selecting next sibling:', nextSibling.dataset.siblingId);
      nextSibling.click();
    } else {
      debugWarn('[BundleAlternatesModal] No non-active sibling options available');
    }
  }

  /**
   * Generate options HTML dynamically from alternatesData
   * Used on checkout page where Liquid doesn't pre-render options
   */
  generateOptionsFromData() {
    if (!this.grid || !this.alternatesData || this.alternatesData.length === 0) {
      debugWarn('[BundleAlternatesModal] Cannot generate options: no grid or data');
      return;
    }
    
    debugLog('[BundleAlternatesModal] Generating', this.alternatesData.length, 'options');
    
    // Clear existing content - use settings for legend text
    const settings = this.settings || loadAlternatesSettings();
    this.grid.innerHTML = `<legend class="visually-hidden">${this.escapeHtml(settings.selectLegend)}</legend>`;
    
    // Generate HTML for each alternate
    const recommendedBadgeText = settings.recommendedBadge;
    const optionsHTML = this.alternatesData.map(alt => {
      const inputId = `alternate-${alt.variantId}`;
      const image = alt.featuredImage || '';
      
      return `
        <div 
          class="bundle-alternates-modal__option"
          data-variant-id="${alt.variantId}"
          data-product-id="${alt.productId}"
          data-product-handle="${alt.productHandle}"
          data-item-number="${alt.itemNumber || ''}"
          data-category="${this.escapeHtml(alt.category || '')}"
          data-category-key="${this.escapeHtml(alt.categoryKey || '')}"
          data-sizing-type="${alt.sizingType || 'standard'}"
          data-item-type="${alt.itemType || 'unknown'}"
          data-variant-title="${this.escapeHtml(alt.variantTitle)}"
          data-variant-available="${alt.variantAvailable}"
          hidden
        >
          <input 
            type="radio" 
            name="bundle-alternate" 
            id="${inputId}"
            value="${alt.variantId}"
            class="bundle-alternates-modal__radio"
            data-variant-id="${alt.variantId}"
            data-item-number="${alt.itemNumber || ''}"
          >
          <label for="${inputId}" class="bundle-alternates-modal__label-card">
            <div class="bundle-alternates-modal__option-image">
              ${image ? `<img src="${image}" alt="${this.escapeHtml(removeVendorFromTitle(alt.productTitle, alt.productVendor))}" loading="lazy">` : ''}
              <span class="bundle-alternates-modal__recommended-badge">${this.escapeHtml(recommendedBadgeText)}</span>
            </div>
            <div class="bundle-alternates-modal__option-info">
              <span class="bundle-alternates-modal__option-vendor">${this.escapeHtml(alt.productVendor || '')}</span>
              <span class="bundle-alternates-modal__option-title">${this.escapeHtml(removeVendorFromTitle(alt.productTitle, alt.productVendor))}</span>
              ${this.extractColor(alt.variantTitle) ? `<span class="bundle-alternates-modal__option-color">Color: ${this.escapeHtml(this.extractColor(alt.variantTitle))}</span>` : ''}
              <div class="bundle-alternates-modal__option-prices">
                <span class="bundle-alternates-modal__option-price">
                  ${this.formatMoney(alt.variantPrice)}
                </span>
                ${alt.variantCompareAtPrice && alt.variantCompareAtPrice > alt.variantPrice
                  ? `<span class="bundle-alternates-modal__option-compare-price">${this.formatMoney(alt.variantCompareAtPrice)}</span>`
                  : ''
                }
              </div>
            </div>
            <div class="bundle-alternates-modal__option-radio-dot" aria-hidden="true"></div>
          </label>
        </div>
      `;
    }).join('');
    
    this.grid.insertAdjacentHTML('beforeend', optionsHTML);
    
    // Re-cache the newly created elements
    this.options = this.querySelectorAll('.bundle-alternates-modal__option');
    this.radios = this.querySelectorAll('.bundle-alternates-modal__radio');
    
    // Re-attach radio listeners
    this.radios?.forEach(radio => {
      radio.addEventListener('change', this.handleRadioChange);
    });
    
    debugLog('[BundleAlternatesModal] Generated', this.options.length, 'option elements');
  }
  
  /**
   * Escape HTML entities for safe rendering
   * @param {string} str 
   * @returns {string}
   */
  escapeHtml(str) {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
  
  /**
   * Add the original/primary item as a swap option (for "swap back" functionality)
   * This allows users who have swapped to an alternate to swap back to their original selection
   * 
   * @param {string|Object} selectedSize - The currently selected size
   * @param {string} sizingType - The sizing type ('standard', 'waist-inseam', 'shoe')
   * @returns {boolean} Whether the original item option was added
   */
  addOriginalItemOption(selectedSize, sizingType) {
    if (!this.originalItem || !this.grid) {
      debugLog('[BundleAlternatesModal] No original item to add');
      return false;
    }
    
    const original = this.originalItem;
    debugLog('[BundleAlternatesModal] Adding original item option:', original.productTitle);
    
    // Clean up any existing original options from previous modal opens
    // Remove from DOM
    const existingOriginalOptions = this.grid.querySelectorAll('[data-is-original="true"]');
    existingOriginalOptions.forEach(opt => opt.remove());
    
    // Remove from alternatesData
    this.alternatesData = this.alternatesData.filter(alt => !alt.isOriginal);
    
    // Check if the original has the selected size available
    // For simplicity, we assume the original item stored has the correct size (it was selected before)
    // The original item in sessionStorage should already be the variant that matched the user's size selection
    
    const settings = this.settings || loadAlternatesSettings();
    const inputId = `alternate-original-${original.variantId}`;
    const image = original.featuredImage || '';
    
    // Create the option HTML - styled the same as other alternates with "Original Selection" badge
    const optionHTML = `
      <div 
        class="bundle-alternates-modal__option"
        data-variant-id="${original.variantId}"
        data-product-id="${original.productId}"
        data-product-handle="${original.productHandle}"
        data-item-number="${original.itemNumber || ''}"
        data-category="${this.escapeHtml(original.category || '')}"
        data-category-key="${this.escapeHtml(original.categoryKey || '')}"
        data-sizing-type="${original.sizingType || 'standard'}"
        data-item-type="${original.itemType || 'unknown'}"
        data-variant-title="${this.escapeHtml(original.variantTitle)}"
        data-variant-available="true"
        data-is-original="true"
      >
        <input 
          type="radio" 
          name="bundle-alternate" 
          id="${inputId}"
          value="${original.variantId}"
          class="bundle-alternates-modal__radio"
          data-variant-id="${original.variantId}"
          data-item-number="${original.itemNumber || ''}"
          data-is-original="true"
        >
        <label for="${inputId}" class="bundle-alternates-modal__label-card">
          <div class="bundle-alternates-modal__option-image">
            ${image ? `<img src="${image}" alt="${this.escapeHtml(removeVendorFromTitle(original.productTitle, original.productVendor))}" loading="lazy">` : ''}
            <span class="bundle-alternates-modal__original-badge">Original Selection</span>
          </div>
          <div class="bundle-alternates-modal__option-info">
            <span class="bundle-alternates-modal__option-vendor">${this.escapeHtml(original.productVendor || '')}</span>
            <span class="bundle-alternates-modal__option-title">${this.escapeHtml(removeVendorFromTitle(original.productTitle, original.productVendor))}</span>
            ${this.extractColor(original.variantTitle) ? `<span class="bundle-alternates-modal__option-color">Color: ${this.escapeHtml(this.extractColor(original.variantTitle))}</span>` : ''}
            <div class="bundle-alternates-modal__option-prices">
              <span class="bundle-alternates-modal__option-price">
                ${this.formatMoney(original.price || original.variantPrice)}
              </span>
              ${original.compareAtPrice && original.compareAtPrice > (original.price || original.variantPrice)
                ? `<span class="bundle-alternates-modal__option-compare-price">${this.formatMoney(original.compareAtPrice)}</span>`
                : ''
              }
            </div>
          </div>
          <div class="bundle-alternates-modal__option-radio-dot" aria-hidden="true"></div>
        </label>
      </div>
    `;
    
    // Insert at the beginning of the grid (after the legend if it exists)
    const legend = this.grid.querySelector('legend');
    if (legend) {
      legend.insertAdjacentHTML('afterend', optionHTML);
    } else {
      this.grid.insertAdjacentHTML('afterbegin', optionHTML);
    }
    
    // Re-cache the elements
    this.options = this.querySelectorAll('.bundle-alternates-modal__option');
    this.radios = this.querySelectorAll('.bundle-alternates-modal__radio');
    
    // Attach listener to the new radio
    const newRadio = this.querySelector(`#${inputId}`);
    if (newRadio) {
      newRadio.addEventListener('change', this.handleRadioChange);
    }
    
    // Also add the original item to alternatesData so handleConfirm can find it
    this.alternatesData.unshift({
      variantId: original.variantId,
      variantTitle: original.variantTitle,
      variantAvailable: true,
      variantPrice: original.price || original.variantPrice,
      variantCompareAtPrice: original.compareAtPrice || 0,
      productId: original.productId,
      productHandle: original.productHandle,
      productTitle: original.productTitle,
      productVendor: original.productVendor || '',
      category: original.category,
      categoryKey: original.categoryKey,
      sizingType: original.sizingType,
      itemType: original.itemType,
      itemNumber: original.itemNumber,
      featuredImage: original.featuredImage,
      isOriginal: true // Mark as original for special handling
    });
    
    debugLog('[BundleAlternatesModal] Original item option added successfully');
    return true;
  }
  
  /**
   * Get the roundCents setting from theme settings
   * @returns {boolean}
   */
  getRoundCentsSetting() {
    try {
      // Try bundle-checkout-settings first (on checkout page)
      let settingsEl = document.getElementById('bundle-checkout-settings');
      if (settingsEl) {
        const parsed = JSON.parse(settingsEl.textContent);
        if (parsed.roundCents !== undefined) return parsed.roundCents !== false;
      }
      
      // Try bundle-product-settings (on PDP page)
      settingsEl = document.getElementById('bundle-product-settings');
      if (settingsEl) {
        const parsed = JSON.parse(settingsEl.textContent);
        if (parsed.roundCents !== undefined) return parsed.roundCents !== false;
      }
    } catch (e) {
      debugWarn('[BundleAlternatesModal] Could not parse roundCents setting:', e);
    }
    return true; // Default to rounding
  }

  /**
   * Format price in cents to currency string
   * @param {number} priceInCents 
   * @param {boolean} roundCents - Whether to round to nearest dollar (optional, uses theme setting if not provided)
   * @returns {string}
   */
  formatMoney(priceInCents, roundCents = null) {
    if (typeof priceInCents !== 'number') return '–';
    
    // Use provided value or fall back to theme setting
    const shouldRound = roundCents !== null ? roundCents : this.getRoundCentsSetting();
    
    const options = {
      style: 'currency',
      currency: 'USD'
    };
    
    if (shouldRound) {
      options.minimumFractionDigits = 0;
      options.maximumFractionDigits = 0;
      // Round to nearest dollar
      priceInCents = Math.round(priceInCents / 100) * 100;
    }
    
    return new Intl.NumberFormat('en-US', options).format(priceInCents / 100);
  }

  /**
   * Extract color from a variant title.
   * Variant titles follow "Size / Color" format. Returns the color portion,
   * or null if the title has no color segment.
   * @param {string} variantTitle
   * @returns {string|null}
   */
  extractColor(variantTitle) {
    if (!variantTitle || typeof variantTitle !== 'string') return null;
    const parts = variantTitle.split(' / ');
    if (parts.length < 2) return null;
    return parts[1].trim() || null;
  }
  
  /**
   * Clear all radio selections and reset visual states
   */
  clearRadioSelections() {
    // Uncheck all radios
    this.radios?.forEach(radio => {
      radio.checked = false;
    });
    
    // Remove selected state from option wrappers
    this.options?.forEach(opt => {
      opt.classList.remove('is-selected');
    });
  }
  
  /**
   * Update the current item display in the header
   * @param {Object} item 
   */
  updateCurrentItemDisplay(item) {
    // Update image
    if (this.currentImageEl) {
      if (item.featuredImage) {
        const cleanTitle = removeVendorFromTitle(item.productTitle, item.productVendor);
        this.currentImageEl.innerHTML = `<img src="${item.featuredImage}" alt="${cleanTitle}" loading="lazy">`;
      } else {
        this.currentImageEl.innerHTML = '<div class="bundle-alternates-modal__placeholder"></div>';
      }
    }
    
    // Update vendor
    const vendorEl = this.querySelector('[data-current-vendor]');
    if (vendorEl) {
      vendorEl.textContent = item.productVendor || '';
      vendorEl.hidden = !item.productVendor;
    }
    
    // Update title (with vendor removed)
    if (this.currentTitleEl) {
      this.currentTitleEl.textContent = removeVendorFromTitle(item.productTitle, item.productVendor);
    }
    
    // Update size (crossed out to show unavailable) - format with inch symbol for waist-inseam
    const sizeEl = this.querySelector('[data-current-size]');
    if (sizeEl && item.selectedSize) {
      sizeEl.textContent = formatSizeForDisplay(item.selectedSize);
      sizeEl.hidden = false;
    } else if (sizeEl) {
      sizeEl.hidden = true;
    }
  }
  
  /**
   * Filter alternates by itemNumber (primary) or category (fallback) and show only those with matching size available
   * Each option element represents a specific variant from the metafield
   * 
   * NEW: Uses position-based matching via itemNumber (1-6):
   * - itemNumber matches the bundle slot position - Item 1 OOS → show item_1_alternates only
   * - This is more reliable than category-based filtering
   * 
   * Falls back to category-aware matching for backwards compatibility:
   * - categoryKey matches the item's categoryKey (e.g., 'jeans', 'polos')
   * - sizingType determines how to match sizes ('standard', 'waist-inseam', 'shoe')
   * 
   * @param {number|null} itemNumber - The bundle item slot number (1-6) to match, or null for legacy mode
   * @param {string} categoryKey - Category key for fallback matching (e.g., 'jeans', 'polos') or legacy itemType
   * @param {string|Object} selectedSize - The size to match
   * @param {string} sizingType - 'standard', 'waist-inseam', or 'shoe'
   * @returns {{ visibleCount: number, positionMatchCount: number }} Visible count and total position-matched alternates (before size filtering)
   */
  filterAlternates(itemNumber, categoryKey, selectedSize, sizingType = 'standard', selectedType = null) {
    debugLog('[BundleAlternatesModal] ========== FILTERING ALTERNATES ==========');
    debugLog('[BundleAlternatesModal] Looking for itemNumber:', itemNumber);
    debugLog('[BundleAlternatesModal] Looking for categoryKey (fallback):', categoryKey);
    debugLog('[BundleAlternatesModal] Looking for selectedSize:', selectedSize);
    debugLog('[BundleAlternatesModal] Looking for sizingType:', sizingType);
    debugLog('[BundleAlternatesModal] Looking for selectedType:', selectedType);
    debugLog('[BundleAlternatesModal] Current item productId (will exclude):', this.currentItem?.productId);
    debugLog('[BundleAlternatesModal] Total alternates in data:', this.alternatesData.length);
    debugLog('[BundleAlternatesModal] Total option elements:', this.options?.length || 0);
    
    let visibleCount = 0;
    let positionMatchCount = 0; // Alternates that match the slot (before size filtering)
    
    // Determine filtering mode
    const useItemNumberFiltering = itemNumber !== null && itemNumber !== undefined;
    const isLegacyMode = !useItemNumberFiltering && ['top', 'bottom', 'shoe', 'unknown'].includes(categoryKey);
    
    debugLog('[BundleAlternatesModal] Using itemNumber filtering:', useItemNumberFiltering);
    debugLog('[BundleAlternatesModal] Using legacy mode:', isLegacyMode);
    
    // Two-tier collection: gather candidates first, then decide which to reveal
    const positionSizeMatched = []; // Options matching position + size (not current item)
    const typeMatched = [];         // Subset also matching selectedType
    
    this.options.forEach((option, index) => {
      // Each option represents a specific variant - match by variantId
      const variantId = option.dataset.variantId;
      const optionItemNumber = option.dataset.itemNumber ? parseInt(option.dataset.itemNumber, 10) : null;
      const alternate = this.alternatesData.find(a => a.variantId?.toString() === variantId);
      
      const radio = option.querySelector('.bundle-alternates-modal__radio');
      
      // Hide everything initially; reset selection state
      option.hidden = true;
      option.classList.remove('is-selected');
      if (radio) radio.checked = false;
      
      debugLog(`[BundleAlternatesModal] --- Option ${index} ---`);
      debugLog(`[BundleAlternatesModal] Variant ID from DOM:`, variantId);
      debugLog(`[BundleAlternatesModal] ItemNumber from DOM:`, optionItemNumber);
      debugLog(`[BundleAlternatesModal] Found in alternatesData:`, !!alternate);
      
      if (!alternate) {
        debugLog(`[BundleAlternatesModal] ✗ No matching alternate data, hiding`);
        return;
      }
      
      debugLog(`[BundleAlternatesModal] Alternate:`, alternate.productTitle, `-`, alternate.variantTitle);
      debugLog(`[BundleAlternatesModal] Alternate itemNumber:`, alternate.itemNumber);
      debugLog(`[BundleAlternatesModal] Alternate categoryKey:`, alternate.categoryKey);
      debugLog(`[BundleAlternatesModal] Alternate productType:`, alternate.productType);
      debugLog(`[BundleAlternatesModal] Alternate sizingType:`, alternate.sizingType);
      debugLog(`[BundleAlternatesModal] Alternate itemType (legacy):`, alternate.itemType);
      debugLog(`[BundleAlternatesModal] Variant available:`, alternate.variantAvailable);
      
      // Determine position/category matching
      let positionMatches = false;
      
      if (useItemNumberFiltering) {
        const alternateItemNumber = alternate.itemNumber || optionItemNumber;
        positionMatches = alternateItemNumber === itemNumber;
        debugLog(`[BundleAlternatesModal] ItemNumber match (${alternateItemNumber} === ${itemNumber}):`, positionMatches);
      } else if (isLegacyMode) {
        const optionType = this.deriveItemType(alternate);
        positionMatches = optionType === categoryKey || 
                          categoryKey === 'unknown' || 
                          optionType === 'unknown';
        debugLog(`[BundleAlternatesModal] Legacy type match (${optionType} === ${categoryKey}):`, positionMatches);
      } else {
        const alternateCategoryKey = alternate.categoryKey || this.deriveCategoryKey(alternate);
        positionMatches = alternateCategoryKey === categoryKey;
        debugLog(`[BundleAlternatesModal] Category match (${alternateCategoryKey} === ${categoryKey}):`, positionMatches);
      }
      
      // Track alternates that match the slot position (before size/availability filtering)
      if (positionMatches) {
        positionMatchCount++;
      }
      
      // Check if this specific variant matches the size and is available
      const sizeAvailable = this.checkVariantSizeMatch(alternate, selectedSize, sizingType);
      debugLog(`[BundleAlternatesModal] Size available:`, sizeAvailable);
      
      // Check if this alternate is the same as the current item
      const isCurrentSeedVariant = this.currentItem && 
        alternate.variantId?.toString() === this.currentItem.variantId?.toString();
      
      let isCurrentInSiblings = false;
      if (this.currentItem && alternate.siblingVariants && alternate.siblingVariants.length > 0) {
        isCurrentInSiblings = alternate.siblingVariants.some(
          sibling => sibling.variantId?.toString() === this.currentItem.variantId?.toString()
        );
      }
      
      const isCurrentItem = isCurrentSeedVariant || isCurrentInSiblings;
      
      if (isCurrentItem) {
        debugLog(`[BundleAlternatesModal] ✗ Skipping alternate (current item)`);
        return;
      }
      
      if (positionMatches && sizeAvailable) {
        positionSizeMatched.push(option);
        
        if (selectedType && alternate.productType === selectedType) {
          typeMatched.push(option);
        }
      }
    });
    
    // Two-tier reveal: prefer type-matched alternates, fall back to all position+size matched
    const optionsToShow = (selectedType && typeMatched.length > 0)
      ? typeMatched
      : positionSizeMatched;
    
    optionsToShow.forEach(opt => { opt.hidden = false; });
    visibleCount = optionsToShow.length;
    
    debugLog('[BundleAlternatesModal] ========== FILTER COMPLETE ==========');
    debugLog('[BundleAlternatesModal] Position+size matched:', positionSizeMatched.length);
    debugLog('[BundleAlternatesModal] Type matched:', typeMatched.length);
    debugLog('[BundleAlternatesModal] Visible alternates:', visibleCount);
    debugLog('[BundleAlternatesModal] Position-matched alternates (before size filter):', positionMatchCount);
    
    // Mark the first visible option as recommended
    this.updateRecommendedBadge();
    
    return { visibleCount, positionMatchCount };
  }
  
  /**
   * Update the recommended badge - show only on the first visible option
   */
  updateRecommendedBadge() {
    let foundFirst = false;
    this.options.forEach(option => {
      const badge = option.querySelector('.bundle-alternates-modal__recommended-badge');
      if (!badge) return;
      
      if (!option.hidden && !foundFirst) {
        // This is the first visible option - show the badge
        badge.classList.add('is-visible');
        foundFirst = true;
      } else {
        // Hide badge on all other options
        badge.classList.remove('is-visible');
      }
    });
  }
  
  /**
   * Derive categoryKey from alternate data (fallback when not provided by Liquid)
   * @param {Object} alternate - Alternate variant data
   * @returns {string} categoryKey
   */
  deriveCategoryKey(alternate) {
    // First try the pre-computed categoryKey from Liquid
    if (alternate.categoryKey) {
      return alternate.categoryKey;
    }
    
    // Fallback: derive from itemType (for backwards compatibility)
    switch (alternate.itemType) {
      case 'top':
        return 'tops';
      case 'bottom':
        return 'bottoms';
      case 'shoe':
        return 'shoes';
      default:
        return 'unknown';
    }
  }
  
  /**
   * Derive sizingType from alternate data
   * @param {Object} alternate - Alternate variant data
   * @returns {string} sizingType ('standard', 'waist-inseam', 'shoe')
   */
  deriveSizingTypeFromAlternate(alternate) {
    // First try the pre-computed sizingType from Liquid
    if (alternate.sizingType) {
      return alternate.sizingType;
    }
    
    // Fallback: derive from itemType
    switch (alternate.itemType) {
      case 'bottom':
        return 'waist-inseam';
      case 'shoe':
        return 'shoe';
      default:
        return 'standard';
    }
  }
  
  /**
   * Derive item type from product options (matches Liquid logic)
   * @param {Object} alternate - Alternate variant data from JSON
   * @returns {string} 'top' | 'bottom' | 'shoe' | 'unknown'
   */
  deriveItemType(alternate) {
    // First, use the pre-computed itemType from Liquid if available
    if (alternate.itemType && alternate.itemType !== 'unknown') {
      return alternate.itemType;
    }
    
    // Fallback: derive from options array
    const options = alternate.options || [];
    for (const optionName of options) {
      const lower = optionName.toLowerCase();
      
      if (lower.includes('top')) {
        return 'top';
      }
      if (lower.includes('bottom') || lower.includes('waist') || lower.includes('pant')) {
        return 'bottom';
      }
      if (lower.includes('shoe') || lower.includes('footwear')) {
        return 'shoe';
      }
    }
    
    // Final fallback: check variant title pattern for bottoms
    if (alternate.variantTitle) {
      if (/\d+x\d+/.test(alternate.variantTitle)) {
        return 'bottom';
      }
    }
    
    return 'unknown';
  }
  
  /**
   * Check if a specific variant matches the required size and is available
   * Each alternate in the new structure represents a single variant
   * 
   * @param {Object} alternate - The alternate variant data (includes variantTitle, variantAvailable, sizingType)
   * @param {string|Object} selectedSize - The size to check
   * @param {string} sizingType - The sizing type for size matching logic ('standard', 'waist-inseam', 'shoe')
   * @returns {boolean}
   */
  checkVariantSizeMatch(alternate, selectedSize, sizingType) {
    debugLog(`[BundleAlternatesModal] checkVariantSizeMatch for "${alternate.productTitle}" - "${alternate.variantTitle}"`);
    debugLog(`[BundleAlternatesModal]   - selectedSize:`, selectedSize);
    debugLog(`[BundleAlternatesModal]   - sizingType:`, sizingType);
    debugLog(`[BundleAlternatesModal]   - variantAvailable:`, alternate.variantAvailable);
    debugLog(`[BundleAlternatesModal]   - siblingVariants:`, alternate.siblingVariants?.length || 0);
    
    // Use the alternate's sizingType if available, otherwise use the passed sizingType
    const effectiveSizingType = alternate.sizingType || sizingType;
    
    // ============================================================
    // NEW: Check siblingVariants for matching size
    // The alternate is a "seed variant" - we need to check if ANY sibling
    // variant (same product, same color) matches the selected size and is available
    // ============================================================
    if (alternate.siblingVariants && alternate.siblingVariants.length > 0) {
      debugLog(`[BundleAlternatesModal]   - Checking ${alternate.siblingVariants.length} sibling variants for size match...`);
      
      for (const sibling of alternate.siblingVariants) {
        // Skip unavailable siblings
        if (!sibling.variantAvailable) {
          debugLog(`[BundleAlternatesModal]     - Sibling "${sibling.variantTitle}" not available, skipping`);
          continue;
        }
        
        // Check if this sibling matches the selected size
        const siblingMatches = this.sizeMatches(sibling.variantTitle, selectedSize, effectiveSizingType);
        if (siblingMatches) {
          debugLog(`[BundleAlternatesModal]     - ✓ Sibling "${sibling.variantTitle}" matches selected size!`);
          // Store the matching sibling variant info on the alternate for later use
          alternate.matchedSiblingVariant = sibling;
          return true;
        } else {
          debugLog(`[BundleAlternatesModal]     - Sibling "${sibling.variantTitle}" does not match`);
        }
      }
      
      debugLog(`[BundleAlternatesModal]   - No sibling variants match the selected size`);
      return false;
    }
    
    // Fallback: No siblingVariants data, check the seed variant directly (legacy behavior)
    debugLog(`[BundleAlternatesModal]   - No siblingVariants, checking seed variant directly`);
    
    // Check if this specific variant is available
    if (!alternate.variantAvailable) {
      debugLog(`[BundleAlternatesModal]   - Seed variant not available, returning false`);
      return false;
    }
    
    // Check if this variant's title matches the selected size
    const matches = this.sizeMatches(alternate.variantTitle, selectedSize, effectiveSizingType);
    debugLog(`[BundleAlternatesModal]   - Size matches:`, matches);
    
    return matches;
  }
  
  /**
   * Check if a variant title matches the selected size
   * Handles formats like "M", "32x30", "10.5", etc.
   * Also handles multi-option variants like "32x30 / Blue" where size is first option
   * 
   * Uses size equivalence mapping from theme settings to match non-standard sizes
   * (e.g., "M" matches "38", "40", "Medium", "15-15.5" for dress shirts, etc.)
   * 
   * @param {string} variantTitle - The variant's title/size (may include color like "32x30 / Blue")
   * @param {string|Object} selectedSize - The size to match
   * @param {string} sizingType - 'standard', 'waist-inseam', or 'shoe'
   * @returns {boolean}
   */
  sizeMatches(variantTitle, selectedSize, sizingType) {
    debugLog(`[BundleAlternatesModal] sizeMatches("${variantTitle}", ${JSON.stringify(selectedSize)}, "${sizingType}")`);
    
    if (!variantTitle || !selectedSize) {
      debugLog(`[BundleAlternatesModal]   - Missing variantTitle or selectedSize, returning false`);
      return false;
    }
    
    // Extract just the size part if variant title includes color (e.g., "32x30 / Sand Bar" → "32x30")
    // Variant titles with multiple options are separated by " / "
    const sizePart = variantTitle.split(' / ')[0].trim();
    debugLog(`[BundleAlternatesModal]   - Extracted size part: "${sizePart}" (from "${variantTitle}")`);
    
    // For waist-inseam sizing (bottoms with waist/inseam object)
    if (sizingType === 'waist-inseam' && typeof selectedSize === 'object') {
      const { waist, inseam, combined } = selectedSize;
      debugLog(`[BundleAlternatesModal]   - Waist-inseam size object: waist=${waist}, inseam=${inseam}, combined=${combined}`);
      
      // Try combined format first (e.g., "32x30")
      if (combined && sizePart === combined) {
        debugLog(`[BundleAlternatesModal]   - ✓ Combined match: "${sizePart}" === "${combined}"`);
        return true;
      }
      
      // Try matching waist and inseam separately
      if (waist && inseam) {
        const expectedCombined = `${waist}x${inseam}`;
        const matches = sizePart === expectedCombined;
        debugLog(`[BundleAlternatesModal]   - Expected combined: "${expectedCombined}", sizePart: "${sizePart}", matches: ${matches}`);
        if (matches) return true;
      }
      
      // Try size equivalence mapping for waist-only match (e.g., shorts without inseam)
      // The waist might be "32" and the sizePart might be "M" or vice versa
      if (waist && sizeMapping.sizesMatch(waist, sizePart)) {
        debugLog(`[BundleAlternatesModal]   - ✓ Waist equivalence match via size mapping: "${waist}" ≈ "${sizePart}"`);
        return true;
      }
      
      debugLog(`[BundleAlternatesModal]   - No waist-inseam match found`);
      return false;
    }
    
    // Handle the case where sizingType is waist-inseam but selectedSize is a string (combined format)
    if (sizingType === 'waist-inseam' && typeof selectedSize === 'string') {
      // selectedSize might be "32x30" format
      const selectedSizeTrimmed = selectedSize.trim();
      if (sizePart === selectedSizeTrimmed) {
        debugLog(`[BundleAlternatesModal]   - ✓ Waist-inseam string match: "${sizePart}" === "${selectedSizeTrimmed}"`);
        return true;
      }
      
      // For single-value waist sizes (shorts), try equivalence mapping
      // Check if selectedSize looks like waist-only (no 'x')
      if (!selectedSizeTrimmed.includes('x') && sizeMapping.sizesMatch(selectedSizeTrimmed, sizePart)) {
        debugLog(`[BundleAlternatesModal]   - ✓ Waist-only equivalence match: "${selectedSizeTrimmed}" ≈ "${sizePart}"`);
        return true;
      }
    }
    
    // For standard and shoe sizing (single-value sizes like "M", "L", "10.5")
    if (typeof selectedSize === 'string') {
      const selectedSizeTrimmed = selectedSize.trim();
      
      // Direct match against size part
      if (sizePart === selectedSizeTrimmed) {
        debugLog(`[BundleAlternatesModal]   - ✓ Size part match: "${sizePart}" === "${selectedSizeTrimmed}"`);
        return true;
      }
      
      // Also check full variant title (in case there's no color option)
      if (variantTitle.trim() === selectedSizeTrimmed) {
        debugLog(`[BundleAlternatesModal]   - ✓ Full title match: "${variantTitle.trim()}" === "${selectedSizeTrimmed}"`);
        return true;
      }
      
      // NEW: Check size equivalence mapping for non-standard sizes
      // This allows matching "M" with "38", "40", "Medium", "15-15.5", etc.
      if (sizeMapping.sizesMatch(selectedSizeTrimmed, sizePart)) {
        debugLog(`[BundleAlternatesModal]   - ✓ Size equivalence match: "${selectedSizeTrimmed}" ≈ "${sizePart}"`);
        return true;
      }
      
      // Handle tailored jacket sizes with fit suffixes (e.g., "38R", "40S", "42L")
      // The "R/S/L" suffix indicates Regular/Short/Long fit and should be stripped for size matching
      const tailoredSizeMatch = sizePart.match(/^(\d+)[RSL]$/i);
      if (tailoredSizeMatch) {
        const numericSize = tailoredSizeMatch[1];
        debugLog(`[BundleAlternatesModal]   - Detected tailored size with suffix, extracted: "${numericSize}" from "${sizePart}"`);
        if (sizeMapping.sizesMatch(selectedSizeTrimmed, numericSize)) {
          debugLog(`[BundleAlternatesModal]   - ✓ Tailored size equivalence match: "${selectedSizeTrimmed}" ≈ "${numericSize}" (from "${sizePart}")`);
          return true;
        }
      }
      
      // Also check if selected size has a suffix that needs to be stripped
      const selectedTailoredMatch = selectedSizeTrimmed.match(/^(\d+)[RSL]$/i);
      if (selectedTailoredMatch) {
        const numericSelected = selectedTailoredMatch[1];
        debugLog(`[BundleAlternatesModal]   - Selected size has tailored suffix, extracted: "${numericSelected}" from "${selectedSizeTrimmed}"`);
        if (sizeMapping.sizesMatch(numericSelected, sizePart)) {
          debugLog(`[BundleAlternatesModal]   - ✓ Tailored size equivalence match: "${numericSelected}" (from "${selectedSizeTrimmed}") ≈ "${sizePart}"`);
          return true;
        }
        // Also check both stripped
        if (tailoredSizeMatch && sizeMapping.sizesMatch(numericSelected, tailoredSizeMatch[1])) {
          debugLog(`[BundleAlternatesModal]   - ✓ Both tailored sizes match: "${numericSelected}" ≈ "${tailoredSizeMatch[1]}"`);
          return true;
        }
      }
      
      debugLog(`[BundleAlternatesModal]   - No string match: sizePart="${sizePart}", selectedSize="${selectedSizeTrimmed}"`);
      return false;
    }
    
    debugLog(`[BundleAlternatesModal]   - Unhandled case, returning false`);
    return false;
  }
  
  /**
   * Toggle empty state visibility
   * @param {boolean} showEmpty 
   */
  toggleEmptyState(showEmpty) {
    if (this.emptyState) {
      this.emptyState.hidden = !showEmpty;
    }
    if (this.grid) {
      this.grid.hidden = showEmpty;
    }
    if (this.confirmBtn) {
      this.confirmBtn.hidden = showEmpty;
    }
    // Show/hide the empty state link (replaces confirm button when empty)
    if (this.emptyLink) {
      this.emptyLink.hidden = !showEmpty;
    }
    // Show/hide the zero-state hyperlink below the CTA
    if (this.zeroStateLink) {
      this.zeroStateLink.hidden = !showEmpty;
    }
  }
  
  /**
   * Handle radio input change
   * @param {Event} event 
   */
  handleRadioChange(event) {
    const radio = event.target;
    const variantId = radio.dataset.variantId || radio.value;
    
    // Find the option wrapper containing this radio
    const option = radio.closest('.bundle-alternates-modal__option');
    
    if (!option || option.hidden) {
      debugWarn('[BundleAlternatesModal] Radio change on hidden/invalid option');
      return;
    }
    
    // Remove selected state from all options
    this.options.forEach(opt => {
      opt.classList.remove('is-selected');
    });
    
    // Add selected state to the chosen option
    option.classList.add('is-selected');
    
    // Store selection - get data from the option wrapper
    this.selectedAlternate = {
      variantId: option.dataset.variantId,
      productId: option.dataset.productId,
      productHandle: option.dataset.productHandle,
      variantTitle: option.dataset.variantTitle
    };
    
    debugLog('[BundleAlternatesModal] Selected alternate:', this.selectedAlternate);
    
    // Enable confirm button now that a selection is made
    if (this.confirmBtn) {
      this.confirmBtn.disabled = false;
    }
  }
  
  /**
   * Handle confirm button click
   * The alternate is a "seed variant" but we may have matched a sibling variant with the correct size
   */
  handleConfirm() {
    if (!this.selectedAlternate || !this.currentItem) {
      debugWarn('[BundleAlternatesModal] Cannot confirm - no selection or current item');
      return;
    }
    
    debugLog('[BundleAlternatesModal] Confirming swap');
    
    const manager = BundleAlternatesManager.getInstance();
    
    // Find the alternate data by variant ID (the seed variant)
    const alternate = this.alternatesData.find(
      a => a.variantId?.toString() === this.selectedAlternate.variantId
    );
    
    if (!alternate) {
      debugError('[BundleAlternatesModal] Could not find alternate data for variant:', this.selectedAlternate.variantId);
      return;
    }
    
    // Check if we have a matched sibling variant (correct size, same color)
    // This was set during checkVariantSizeMatch when we found a matching sibling
    const matchedSibling = alternate.matchedSiblingVariant;
    
    if (matchedSibling) {
      debugLog('[BundleAlternatesModal] Using matched sibling variant:', matchedSibling.variantTitle);
      // Use the sibling variant's ID, title, and image (if available), but keep product info from the alternate
      manager.confirmSwap(matchedSibling.variantId, {
        productId: alternate.productId,
        productHandle: alternate.productHandle,
        productTitle: alternate.productTitle,
        productVendor: alternate.productVendor || '',
        variantTitle: matchedSibling.variantTitle,
        price: matchedSibling.variantPrice,
        compareAtPrice: alternate.variantCompareAtPrice || 0,
        // Prefer sibling variant's image over product featured image
        featuredImage: matchedSibling.variantImage || alternate.featuredImage
      });
    } else {
      debugLog('[BundleAlternatesModal] Using seed variant (no sibling match):', alternate.variantTitle);
      // Fallback: use the seed variant directly (legacy behavior)
      manager.confirmSwap(alternate.variantId, {
        productId: alternate.productId,
        productHandle: alternate.productHandle,
        productTitle: alternate.productTitle,
        productVendor: alternate.productVendor || '',
        variantTitle: alternate.variantTitle,
        price: alternate.variantPrice,
        compareAtPrice: alternate.variantCompareAtPrice || 0,
        // Use variant image if available, otherwise product featured image
        featuredImage: alternate.variantImage || alternate.featuredImage
      });
    }
  }
  
  /**
   * Handle close button click (cancel all)
   */
  handleClose() {
    debugLog('[BundleAlternatesModal] Closing modal (cancel all)');
    BundleAlternatesManager.getInstance().cancelAll();
  }
  
  /**
   * Handle overlay click
   * @param {Event} event 
   */
  handleOverlayClick(event) {
    if (event.target === this.overlay) {
      this.handleClose();
    }
  }
  
  /**
   * Handle keyboard events
   * @param {KeyboardEvent} event 
   */
  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.handleClose();
    }
  }
  
  /**
   * Handle Enter/Space on label cards to select the associated radio
   * @param {KeyboardEvent} event 
   */
  handleLabelKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      // Click the label to trigger its associated radio input
      event.currentTarget.click();
    }
  }
  
  /**
   * Handle focus trap - keep focus within the modal
   * @param {KeyboardEvent} event 
   */
  handleFocusTrap(event) {
    if (event.key !== 'Tab') return;
    
    const modalContainer = this.querySelector('.bundle-alternates-modal__container');
    if (!modalContainer) return;
    
    // Always prevent default Tab behavior and manage focus manually
    event.preventDefault();
    
    // Get all focusable elements within the modal container
    // Exclude elements with tabindex="-1" as they should not be in the tab order
    const focusableSelectors = [
      'button:not([disabled]):not([tabindex="-1"])',
      'input:not([disabled]):not([tabindex="-1"])',
      '[tabindex]:not([tabindex="-1"]):not([disabled])',
      'a[href]:not([disabled]):not([tabindex="-1"])'
    ].join(', ');
    
    const focusableElements = modalContainer.querySelectorAll(focusableSelectors);
    
    // Filter to only visible, not-hidden elements
    const visibleFocusable = Array.from(focusableElements).filter(el => {
      // Check if the element or any parent is hidden
      if (el.closest('[hidden]')) return false;
      // Check if element is visible
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });
    
    if (visibleFocusable.length === 0) return;
    
    const firstFocusable = visibleFocusable[0];
    const lastFocusable = visibleFocusable[visibleFocusable.length - 1];
    
    // Find current focus index
    const currentIndex = visibleFocusable.indexOf(document.activeElement);
    
    if (event.shiftKey) {
      // Shift+Tab: go backwards
      if (currentIndex <= 0 || currentIndex === -1) {
        // At first element or outside modal -> go to last
        lastFocusable.focus();
      } else {
        visibleFocusable[currentIndex - 1].focus();
      }
    } else {
      // Tab: go forwards
      if (currentIndex === -1 || currentIndex >= visibleFocusable.length - 1) {
        // At last element or outside modal -> go to first
        firstFocusable.focus();
      } else {
        visibleFocusable[currentIndex + 1].focus();
      }
    }
  }
  
  /**
   * Show the modal
   */
  show() {
    // Store the currently focused element to restore later
    this.previouslyFocusedElement = document.activeElement;
    
    this.setAttribute('aria-hidden', 'false');
    this.setAttribute('data-mode', this.currentMode || 'oos');
    this.classList.add('is-open');
    document.body.classList.add('bundle-alternates-modal-open');
    document.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('keydown', this.handleFocusTrap);
    
    // Focus first interactive element
    requestAnimationFrame(() => {
      const firstOption = this.querySelector('.bundle-alternates-modal__option:not([hidden])');
      if (firstOption) {
        const radio = firstOption.querySelector('.bundle-alternates-modal__radio');
        if (radio) {
          radio.focus();
        } else {
          firstOption.focus();
        }
      } else if (this.closeBtn) {
        this.closeBtn.focus();
      }
    });
    
    debugLog('[BundleAlternatesModal] Modal shown');
  }
  
  /**
   * Hide the modal
   */
  hide() {
    this.setAttribute('aria-hidden', 'true');
    this.removeAttribute('data-mode');
    this.classList.remove('is-open');
    document.body.classList.remove('bundle-alternates-modal-open');
    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('keydown', this.handleFocusTrap);
    
    // Restore focus without scrolling the page (preventScroll avoids mobile jump).
    // Prefer the element that originally opened the modal; fall back to continue button.
    if (this.previouslyFocusedElement && this.previouslyFocusedElement.isConnected) {
      this.previouslyFocusedElement.focus({ preventScroll: true });
    } else {
      const continueBtn = document.querySelector('[data-checkout-button], [data-continue-button], .bundle-product__add-button');
      if (continueBtn) {
        continueBtn.focus({ preventScroll: true });
      }
    }
    
    // Reset selection state
    this.selectedAlternate = null;
    this.currentItem = null;
    this.previouslyFocusedElement = null;
    
    debugLog('[BundleAlternatesModal] Modal hidden');
  }

  // ==========================================================================
  // DEBUG/TESTING HELPERS
  // ==========================================================================

  /**
   * Debug: Log current modal state
   * Call from console: document.querySelector('bundle-alternates-modal').debugState()
   */
  debugState() {
    debugLog('[BundleAlternatesModal] 🧪 DEBUG: Current State');
    debugLog('─'.repeat(60));
    debugLog('Alternates Data Count:', this.alternatesData.length);
    debugLog('Alternates by ItemNumber:', this.getAlternatesByItemNumber());
    debugLog('Current Item:', this.currentItem);
    debugLog('Selected Alternate:', this.selectedAlternate);
    debugLog('Option Elements:', this.options?.length || 0);
    debugLog('─'.repeat(60));
    
    if (this.currentItem) {
      debugLog('Current Item Details:');
      debugLog(`  ItemNumber: ${this.currentItem.itemNumber || 'N/A'}`);
      debugLog(`  Category: ${this.currentItem.category || 'N/A'}`);
      debugLog(`  CategoryKey: ${this.currentItem.categoryKey || 'N/A'}`);
      debugLog(`  SizingType: ${this.currentItem.sizingType || 'N/A'}`);
      debugLog(`  ItemType (legacy): ${this.currentItem.itemType || 'N/A'}`);
      debugLog(`  SelectedSize:`, this.currentItem.selectedSize);
      debugLog('─'.repeat(60));
    }
    
    if (this.alternatesData.length > 0) {
      debugLog('Alternates Data (each entry = one variant):');
      this.alternatesData.forEach((alt, i) => {
        debugLog(`  [${i}] ${alt.productTitle} - "${alt.variantTitle}"`);
        debugLog(`      variantId: ${alt.variantId}`);
        debugLog(`      itemNumber: ${alt.itemNumber || 'N/A'}`);
        debugLog(`      category: ${alt.category || 'N/A'}`);
        debugLog(`      categoryKey: ${alt.categoryKey || 'N/A'}`);
        debugLog(`      sizingType: ${alt.sizingType || 'N/A'}`);
        debugLog(`      itemType (legacy): ${alt.itemType}`);
        debugLog(`      available: ${alt.variantAvailable}`);
        debugLog(`      price: ${alt.variantPrice}`);
      });
    }
    
    debugLog('─'.repeat(60));
    return {
      alternatesCount: this.alternatesData.length,
      alternatesByItemNumber: this.getAlternatesByItemNumber(),
      alternatesData: this.alternatesData,
      currentItem: this.currentItem,
      optionElementsCount: this.options?.length || 0
    };
  }

  /**
   * Debug: Manually test filtering with specific values
   * NEW signature: testFilter(itemNumber, categoryKey, selectedSize, sizingType)
   * 
   * Call from console with itemNumber (new method):
   *   document.querySelector('bundle-alternates-modal').testFilter(1, null, 'M', 'standard')
   * 
   * Call without itemNumber (falls back to category):
   *   document.querySelector('bundle-alternates-modal').testFilter(null, 'jeans', 'M', 'standard')
   * 
   * For waist-inseam:
   *   testFilter(1, null, { waist: '32', inseam: '30', combined: '32x30' }, 'waist-inseam')
   */
  testFilter(itemNumber, categoryKey, selectedSize, sizingType = 'standard', selectedType = null) {
    debugLog('[BundleAlternatesModal] 🧪 DEBUG: Testing filter');
    debugLog('  itemNumber:', itemNumber);
    debugLog('  categoryKey:', categoryKey);
    debugLog('  selectedSize:', selectedSize);
    debugLog('  sizingType:', sizingType);
    debugLog('  selectedType:', selectedType);
    return this.filterAlternates(itemNumber, categoryKey, selectedSize, sizingType, selectedType);
  }

  /**
   * Debug: Get the raw JSON from the script tag
   * Call from console: document.querySelector('bundle-alternates-modal').getRawJSON()
   */
  getRawJSON() {
    const dataEl = this.querySelector('[data-alternates-json]');
    if (dataEl) {
      try {
        return JSON.parse(dataEl.textContent);
      } catch (e) {
        debugError('Failed to parse JSON:', e);
        return dataEl.textContent;
      }
    }
    return null;
  }

  /**
   * Debug: Test size mapping functionality
   * Call from console: document.querySelector('bundle-alternates-modal').testSizeMapping('M', '40')
   * 
   * @param {string} size1 - First size to compare
   * @param {string} size2 - Second size to compare
   */
  testSizeMapping(size1, size2) {
    debugLog('[BundleAlternatesModal] 🧪 DEBUG: Testing size mapping');
    return sizeMapping.testMatch(size1, size2);
  }

  /**
   * Debug: View all loaded size equivalence mappings
   * Call from console: document.querySelector('bundle-alternates-modal').debugSizeMapping()
   */
  debugSizeMapping() {
    debugLog('[BundleAlternatesModal] 🧪 DEBUG: Size mapping state');
    sizeMapping.debug();
    return sizeMapping;
  }
}


// ============================================================================
// REGISTER CUSTOM ELEMENTS
// ============================================================================
if (!customElements.get('bundle-alternates-modal')) {
  customElements.define('bundle-alternates-modal', BundleAlternatesModal);
}


// ============================================================================
// GLOBAL EXPORTS
// ============================================================================
if (typeof window !== 'undefined') {
  window.BundleAlternatesManager = BundleAlternatesManager;
  window.BundleAlternatesModal = BundleAlternatesModal;
}


// ============================================================================
// ES MODULE EXPORTS
// ============================================================================
export { BundleAlternatesManager, BundleAlternatesModal };

